export interface SummaryRow {
  readonly label: string;
  readonly value: string;
}

export interface JobSummaryRowsProps {
  readonly rows: readonly SummaryRow[];
}

/**
 * The five label/value rows above the description [KJM-3; Figma `Summary` 12021:40786].
 *
 * A description list, not a stack of divs: the pairing IS the meaning and `<dl>` is the element that says so
 * to a screen reader. Each row 48px with a 16px inset, a hairline above every row after the first, the value
 * right-aligned and bold against a muted label — read off the frame.
 *
 * **Rows with no value are not rendered.** KJM-3 lists five, and three of them can legitimately be absent:
 * a shift with no times [decision 0083], a pay rate Bullhorn does not carry, and a job type the backend
 * withheld because it was `Evergreen` [decision 0040]. An empty row reads as missing data on a screen a
 * stranger is deciding from; one fewer row reads as a job with less to say about itself.
 *
 * ⚠️ **This duplicates `features/associate/components/DetailList`**, which KAS-1 built to the same frame
 * component. The two are the same 48px row and § 4 says a second consumer promotes it to `src/shared/` —
 * that is not done here because KAS-1's file does not exist on this branch, and a cross-lane import of a
 * file that is not there does not compile. **They should collapse into one when the branches meet**, which
 * is a rename, which is the one conflict a rebase cannot resolve — so it is a deliberate follow-up rather
 * than a silent divergence.
 */
export function JobSummaryRows({ rows }: JobSummaryRowsProps) {
  return (
    /*
     * An inset ring, not a `border` — the same fix `DetailList` already carries against the same frame.
     * `08 · Job detail` draws Summary as 448x240, five 48px rows with the outline INSIDE the box; a CSS
     * border on an auto-height element adds to it instead, so this rendered 242 and pushed the footer down
     * with it. Two pixels here and two on the accordion were 4 of the 30 that separated this screen from
     * its frame.
     */
    <dl className="flex w-full flex-col rounded-[var(--radius-md)] bg-[var(--card)] shadow-[inset_0_0_0_1px_var(--divider-border)]">
      {rows.map((row, index) => (
        <div
          className={`flex h-12 w-full items-center justify-between gap-4 px-4 py-3 ${
            index === 0 ? '' : 'border-t border-solid border-[var(--divider-border)]'
          }`}
          key={row.label}
        >
          <dt className="shrink-0 text-sm leading-5 text-[var(--muted-foreground)]">{row.label}</dt>
          {/*
            `text-right` with `min-w-0`: the location is the longest value here and the frame keeps it on one
            line at 820. Letting it push the label instead of truncating would break the row grid below that.
          */}
          <dd className="min-w-0 text-right text-sm font-bold leading-5 text-[var(--foreground)]">
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
